"use client"

import { cn } from "@/lib/utils"
import {
  Skeleton,
  CardSkeleton as UICardSkeleton,
  TableSkeleton as UITableSkeleton,
} from "@/components/ui/skeleton"

// ============================================================================
// Types
// ============================================================================

interface LoadingStateProps {
  /** Display variant: spinner (animated), skeleton (placeholder), dots (animated dots) */
  variant?: "spinner" | "skeleton" | "dots"
  /** Size preset: sm (inline), md (component), lg (section), full (fullpage centered) */
  size?: "sm" | "md" | "lg" | "full"
  /** Optional message to display below the loading indicator */
  message?: string
  /** Additional CSS classes */
  className?: string
}

interface SpinnerProps {
  /** Size preset matching parent component */
  size?: "sm" | "md" | "lg"
  /** Additional CSS classes */
  className?: string
}

interface CardSkeletonProps {
  /** Number of cards to display */
  count?: number
  /** Additional CSS classes */
  className?: string
}

interface ListSkeletonProps {
  /** Number of rows to display */
  rows?: number
  /** Show avatar placeholder */
  showAvatar?: boolean
  /** Additional CSS classes */
  className?: string
}

interface TableSkeletonProps {
  /** Number of rows to display */
  rows?: number
  /** Number of columns to display */
  cols?: number
  /** Show table header */
  showHeader?: boolean
  /** Additional CSS classes */
  className?: string
}

// ============================================================================
// Cherish Brand Colors
// ============================================================================

const CHERISH_BLUE = "#1a3e6a"

// ============================================================================
// Size Mappings
// ============================================================================

const spinnerSizes = {
  sm: "h-4 w-4 border-2",
  md: "h-8 w-8 border-3",
  lg: "h-12 w-12 border-4",
}

const dotSizes = {
  sm: "h-1.5 w-1.5",
  md: "h-2.5 w-2.5",
  lg: "h-3.5 w-3.5",
}

const containerSizes = {
  sm: "min-h-[24px]",
  md: "min-h-[64px]",
  lg: "min-h-[120px]",
  full: "min-h-screen",
}

// ============================================================================
// Spinner Component
// ============================================================================

function Spinner({ size = "md", className }: SpinnerProps) {
  return (
    <div
      className={cn(
        "rounded-full animate-spin",
        spinnerSizes[size],
        className
      )}
      style={{
        borderColor: `${CHERISH_BLUE}20`,
        borderTopColor: CHERISH_BLUE,
      }}
      role="status"
      aria-label="Loading"
    >
      <span className="sr-only">Loading...</span>
    </div>
  )
}

// ============================================================================
// Dots Component (Alternative Loading Animation)
// ============================================================================

function Dots({ size = "md", className }: SpinnerProps) {
  return (
    <div
      className={cn("flex items-center gap-1", className)}
      role="status"
      aria-label="Loading"
    >
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className={cn(
            "rounded-full animate-bounce",
            dotSizes[size]
          )}
          style={{
            backgroundColor: CHERISH_BLUE,
            animationDelay: `${i * 150}ms`,
            animationDuration: "600ms",
          }}
        />
      ))}
      <span className="sr-only">Loading...</span>
    </div>
  )
}

// ============================================================================
// Card Skeleton
// ============================================================================

function CardSkeletonComponent({ count = 1, className }: CardSkeletonProps) {
  if (count === 1) {
    return <UICardSkeleton className={className} />
  }

  return (
    <div className={cn("grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4", className)}>
      {Array.from({ length: count }).map((_, i) => (
        <UICardSkeleton key={i} />
      ))}
    </div>
  )
}

// ============================================================================
// List Skeleton
// ============================================================================

function ListSkeletonComponent({ rows = 5, showAvatar = true, className }: ListSkeletonProps) {
  return (
    <div className={cn("space-y-3", className)}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 p-3 bg-white rounded-lg border">
          {showAvatar && (
            <Skeleton className="h-10 w-10 rounded-full shrink-0" />
          )}
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
          <Skeleton className="h-8 w-16 shrink-0" />
        </div>
      ))}
    </div>
  )
}

// ============================================================================
// Table Skeleton
// ============================================================================

function TableSkeletonComponent({ rows = 5, cols = 4, showHeader = true, className }: TableSkeletonProps) {
  // For simple cases, use the existing UI component
  if (cols === 4 && showHeader) {
    return <UITableSkeleton rows={rows} />
  }

  // Custom table skeleton with configurable columns
  return (
    <div className={cn("bg-white rounded-xl border shadow-sm overflow-hidden", className)}>
      {showHeader && (
        <div className="flex items-center gap-4 p-4 border-b bg-slate-50">
          {Array.from({ length: cols }).map((_, i) => (
            <Skeleton
              key={i}
              className={cn(
                "h-4",
                i === 0 ? "w-1/4" : "flex-1"
              )}
            />
          ))}
        </div>
      )}
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="flex items-center gap-4 p-4 border-b last:border-b-0">
          {Array.from({ length: cols }).map((_, colIndex) => (
            <Skeleton
              key={colIndex}
              className={cn(
                "h-4",
                colIndex === 0 ? "w-1/4" : "flex-1"
              )}
            />
          ))}
        </div>
      ))}
    </div>
  )
}

// ============================================================================
// Main LoadingState Component
// ============================================================================

function LoadingState({
  variant = "spinner",
  size = "md",
  message,
  className,
}: LoadingStateProps) {
  const isFullPage = size === "full"
  const effectiveSpinnerSize = isFullPage ? "lg" : size === "lg" ? "lg" : size === "md" ? "md" : "sm"

  // Skeleton variant (basic placeholder)
  if (variant === "skeleton") {
    return (
      <div aria-busy="true" aria-live="polite" className={cn("space-y-3", containerSizes[size], className)}>
        <Skeleton className="h-6 w-1/3" />
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    )
  }

  // Dots variant
  if (variant === "dots") {
    return (
      <div
        aria-busy="true"
        aria-live="polite"
        className={cn(
          "flex flex-col items-center justify-center",
          containerSizes[size],
          isFullPage && "fixed inset-0 bg-white/80 backdrop-blur-sm z-50",
          className
        )}
      >
        <Dots size={effectiveSpinnerSize} />
        {message && (
          <p className="mt-3 text-sm text-slate-600 font-medium">{message}</p>
        )}
      </div>
    )
  }

  // Default: Spinner variant
  return (
    <div
      aria-busy="true"
      aria-live="polite"
      className={cn(
        "flex flex-col items-center justify-center",
        containerSizes[size],
        isFullPage && "fixed inset-0 bg-white/80 backdrop-blur-sm z-50",
        className
      )}
    >
      <Spinner size={effectiveSpinnerSize} />
      {message && (
        <p className="mt-3 text-sm text-slate-600 font-medium">{message}</p>
      )}
    </div>
  )
}

// ============================================================================
// Attach Subcomponents
// ============================================================================

LoadingState.Spinner = Spinner
LoadingState.Dots = Dots
LoadingState.CardSkeleton = CardSkeletonComponent
LoadingState.ListSkeleton = ListSkeletonComponent
LoadingState.TableSkeleton = TableSkeletonComponent

// ============================================================================
// Exports
// ============================================================================

export { LoadingState }
export type {
  LoadingStateProps,
  SpinnerProps,
  CardSkeletonProps,
  ListSkeletonProps,
  TableSkeletonProps,
}
